import {
  DeleteAnimationConnectionsCommand,
  DeleteAnimationNodesCommand,
  InsertAnimationNodeCommand,
  MoveAnimationNodesCommand,
  UpdateAnimationNodeCommand,
  UpdateAnimationVisualGroupsCommand,
  UpsertAnimationConnectionCommand,
} from "@/commands/animationCommands";
import { BakeAnimationFrameCommand } from "@/commands/nodeCommands";
import { TransactionCommand } from "@/commands/transaction";
import { animationBakeFrameId } from "@/document/animation/bakeAnimationFrame";
import {
  createAnimationGraphNode,
  createEmptyAnimationGraph,
  normalizeAnimationGraph,
  type AnimateAnimationNode,
  type AnimationConnection,
  type AnimationEndpoint,
  type AnimationGraph,
  type AnimationGraphNode,
  type AnimationGraphVisualGroup,
  type GroupAnimationNode,
  type AnimationTarget,
} from "@/document/model/animation";
import { findSceneNode } from "@/document/model/document";
import { documentStore, selectionStore, uiStore } from "@/editor/store";
import { createId } from "@/lib/ids";
import type { EditorCommandHost } from "../editorCommandHost";
import { animationTargetKey } from "@/editor/shell/animationTargetFromSelection";
import { activateAnimationRootForNode, selectAnimationNodes } from "./selectionHandlers";

interface ParsedObjectLinkKey {
  target: AnimationTarget;
  animNodeId: string;
  pin: string;
  index: number;
}

function parseObjectLinkKey(key: string): ParsedObjectLinkKey | undefined {
  const parts = key.split(":");
  if (parts.length < 4) return undefined;
  const kind = parts[0]!;
  let target: AnimationTarget;
  let rest: string[];
  switch (kind) {
    case "sceneNode": {
      target = { kind: "sceneNode", nodeId: parts[1]! };
      rest = parts.slice(2);
      break;
    }
    case "vectorVertex": {
      if (parts.length < 5) return undefined;
      target = { kind: "vectorVertex", nodeId: parts[1]!, vertexId: parts[2]! };
      rest = parts.slice(3);
      break;
    }
    case "vectorEdge": {
      if (parts.length < 5) return undefined;
      target = { kind: "vectorEdge", nodeId: parts[1]!, edgeId: parts[2]! };
      rest = parts.slice(3);
      break;
    }
    case "vectorHandle": {
      if (parts.length < 6) return undefined;
      target = { kind: "vectorHandle", nodeId: parts[1]!, edgeId: parts[2]!, endpoint: parts[3] as "from" | "to" };
      rest = parts.slice(4);
      break;
    }
    default:
      return undefined;
  }
  const [animNodeId, pin, indexStr] = rest;
  if (!animNodeId || !pin) return undefined;
  return { target, animNodeId, pin, index: Number(indexStr ?? 0) };
}

function animationGraph(): AnimationGraph {
  return normalizeAnimationGraph(documentStore.getDocument().animation ?? createEmptyAnimationGraph());
}

export function duplicateSelectedAnimationNodes(
  host: EditorCommandHost,
  targetPositions?: Array<{ nodeId: string; position: { x: number; y: number } }>,
): void {
  const graph = animationGraph();
  const selectedNodeIds = host.currentSelection().selectedAnimationNodeIds ?? [];
  if (selectedNodeIds.length === 0) return;

  const nodes = graph.nodes.filter(node => selectedNodeIds.includes(node.id));
  const nodeIdSet = new Set(nodes.map(node => node.id));
  const connections = graph.connections.filter(connection => nodeIdSet.has(connection.from.nodeId) && nodeIdSet.has(connection.to.nodeId));
  const positionById = targetPositions ? new Map(targetPositions.map(position => [position.nodeId, position.position])) : undefined;
  const idMap = new Map<string, string>();
  const newNodes = nodes.map(node => {
    const id = createId("animNode");
    idMap.set(node.id, id);
    return {
      ...structuredClone(node),
      id,
      position: positionById?.get(node.id) ?? { x: node.position.x + 20, y: node.position.y + 20 },
    };
  });
  const newConnections = connections.map(connection => ({
    ...structuredClone(connection),
    id: createId("animConn"),
    from: { ...connection.from, nodeId: idMap.get(connection.from.nodeId) ?? connection.from.nodeId },
    to: { ...connection.to, nodeId: idMap.get(connection.to.nodeId) ?? connection.to.nodeId },
  }));
  const context = host.commandContext();
  const newNodeIds = newNodes.map(node => node.id);
  const nextSelection = host.selectionSnapshot({
    selectedAnimationNodeIds: newNodeIds,
    primarySelectedAnimationNodeId: newNodeIds.at(-1),
  });
  const commands = [
    ...newNodes.map(node => new InsertAnimationNodeCommand(node)),
    ...newConnections.map(connection => new UpsertAnimationConnectionCommand(connection)),
  ];
  host.history.execute(commands.length === 1 ? commands[0]! : new TransactionCommand("Duplicate animation nodes", commands), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, newNodes.length > 1 ? `Duplicated ${newNodes.length} animation nodes` : "Duplicated animation node");
}

export function insertAnimationNode(host: EditorCommandHost, node: AnimationGraphNode): void {
  const context = host.commandContext();
  const nextSelection = host.selectionSnapshot({
    selectedAnimationNodeIds: [node.id],
    primarySelectedAnimationNodeId: node.id,
    selectedAnimationConnectionIds: [],
  });
  host.history.execute(new InsertAnimationNodeCommand(node), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  if (node.kind === "animation") activateAnimationRootForNode(node.id);
  host.commit(context.document, `Created ${node.name}`);
}

export function updateAnimationNode(host: EditorCommandHost, nextNode: AnimationGraphNode, label = "Updated animation node"): void {
  const before = animationGraph().nodes.find(node => node.id === nextNode.id);
  if (!before) {
    uiStore.setMessage("Animation node not found");
    return;
  }
  const context = host.commandContext();
  host.history.execute(new UpdateAnimationNodeCommand(nextNode.id, before, nextNode, label), context, { afterSelection: host.currentSelection() });
  host.commit(context.document, label);
}

export function moveAnimationNodes(
  host: EditorCommandHost,
  before: Array<{ nodeId: string; position: { x: number; y: number } }>,
  after: Array<{ nodeId: string; position: { x: number; y: number } }>,
): void {
  const context = host.commandContext();
  host.history.execute(new MoveAnimationNodesCommand(before, after), context, { afterSelection: host.currentSelection() });
  host.commit(context.document, "Moved animation nodes");
}

export function updateAnimationVisualGroups(host: EditorCommandHost, after: AnimationGraphVisualGroup[], label = "Updated animation groups"): void {
  const context = host.commandContext();
  host.history.execute(
    new UpdateAnimationVisualGroupsCommand(structuredClone(animationGraph().visualGroups ?? []), after, label),
    context,
    { afterSelection: host.currentSelection() },
  );
  host.commit(context.document, label);
}

export function updateAnimationVisualGroup(host: EditorCommandHost, nextGroup: AnimationGraphVisualGroup, label = "Updated animation group"): void {
  const groups = animationGraph().visualGroups ?? [];
  if (!groups.some(group => group.id === nextGroup.id)) {
    uiStore.setMessage("Animation group not found");
    return;
  }
  updateAnimationVisualGroups(host, groups.map(group => group.id === nextGroup.id ? nextGroup : group), label);
}

export function deleteAnimationVisualGroup(host: EditorCommandHost, groupId: string): void {
  const groups = animationGraph().visualGroups ?? [];
  if (!groups.some(group => group.id === groupId)) {
    uiStore.setMessage("Animation group not found");
    return;
  }
  updateAnimationVisualGroups(host, groups.filter(group => group.id !== groupId), "Ungrouped animation nodes");
}

export function groupSelectedAnimationNodes(host: EditorCommandHost): void {
  const graph = animationGraph();
  const selectedIds = [...new Set(host.currentSelection().selectedAnimationNodeIds ?? [])].filter(nodeId => graph.nodes.some(node => node.id === nodeId));
  if (selectedIds.length < 2) {
    uiStore.setMessage("Select two or more animation nodes to group");
    return;
  }
  const selected = new Set(selectedIds);
  const groups = (graph.visualGroups ?? [])
    .map(group => ({ ...group, nodeIds: group.nodeIds.filter(nodeId => !selected.has(nodeId)) }))
    .filter(group => group.nodeIds.length > 0);
  groups.push({ id: createId("animGroup"), name: "Group", nodeIds: selectedIds, collapsed: false });
  updateAnimationVisualGroups(host, groups, "Grouped animation nodes");
}

export function connectAnimation(host: EditorCommandHost, connection: AnimationConnection): void {
  const nextConnection = connectionWithAvailableMotionInput(animationGraph(), connection);
  const context = host.commandContext();
  const nextSelection = host.selectionSnapshot({
    selectedAnimationNodeIds: [nextConnection.to.nodeId],
    primarySelectedAnimationNodeId: nextConnection.to.nodeId,
    selectedAnimationConnectionIds: [],
  });
  host.history.execute(new UpsertAnimationConnectionCommand(nextConnection), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, "Connected animation nodes");
}

export function insertAnimationNodeAndConnect(host: EditorCommandHost, node: AnimationGraphNode, connection: AnimationConnection): void {
  const graph = animationGraph();
  const nextConnection = connectionWithAvailableMotionInput({ ...graph, nodes: [...graph.nodes, node] }, connection);
  const context = host.commandContext();
  const nextSelection = host.selectionSnapshot({
    selectedAnimationNodeIds: [node.id],
    primarySelectedAnimationNodeId: node.id,
    selectedAnimationConnectionIds: [],
  });
  host.history.execute(new TransactionCommand(`Created ${node.name}`, [
    new InsertAnimationNodeCommand(node),
    new UpsertAnimationConnectionCommand(nextConnection),
  ]), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, `Created ${node.name}`);
}

export function createSceneObjectAnimateNode(host: EditorCommandHost, sourceNodeId: string, position: { x: number; y: number }): void {
  const source = findSceneNode(documentStore.getDocument(), sourceNodeId);
  if (!source) return;
  if (source.type === "frame") {
    insertAnimationNode(host, createAnimationGraphNode("animation", position, { frameId: sourceNodeId }));
    return;
  }
  const node = createAnimationGraphNode("animate", position) as AnimateAnimationNode;
  insertAnimationNode(host, { ...node, target: { kind: "sceneNode", nodeId: sourceNodeId } });
}

export function createSceneObjectsGroupNode(host: EditorCommandHost, sourceNodeIds: string[], position: { x: number; y: number }): void {
  const targets = sourceNodeIds.filter(nodeId => findSceneNode(documentStore.getDocument(), nodeId));
  if (targets.length === 0) return;
  const node = createAnimationGraphNode("group", position) as GroupAnimationNode;
  insertAnimationNode(host, { ...node, targets: targets.map(nodeId => ({ kind: "sceneNode" as const, nodeId })) });
}

export function createAnimationTargetsNode(host: EditorCommandHost, targets: AnimationTarget[], position: { x: number; y: number }): void {
  if (targets.length === 0) return;
  if (targets.length === 1) {
    const target = targets[0]!;
    if (target.kind === "sceneNode" && findSceneNode(documentStore.getDocument(), target.nodeId)?.type === "frame") {
      insertAnimationNode(host, createAnimationGraphNode("animation", position, { frameId: target.nodeId }));
      return;
    }
    const node = createAnimationGraphNode("animate", position) as AnimateAnimationNode;
    insertAnimationNode(host, { ...node, target });
    return;
  }
  const node = createAnimationGraphNode("group", position) as GroupAnimationNode;
  insertAnimationNode(host, { ...node, targets });
}

export function connectAnimationTargetsToAnimation(host: EditorCommandHost, targets: AnimationTarget[], endpoint: AnimationEndpoint): void {
  if (targets.length === 0) return;
  if (targets.length === 1) {
    const target = targets[0]!;
    if (target.kind === "sceneNode") {
      connectSceneObjectToAnimation(host, target.nodeId, endpoint);
      return;
    }
    connectSingleTargetToAnimation(host, target, endpoint);
    return;
  }
  connectMultipleTargetsToAnimation(host, targets, endpoint);
}

function connectSingleTargetToAnimation(host: EditorCommandHost, target: AnimationTarget, endpoint: AnimationEndpoint): void {
  const node = animationGraph().nodes.find(candidate => candidate.id === endpoint.nodeId);
  if (!node) {
    uiStore.setMessage("Animation connection target not found");
    return;
  }

  if (node.kind === "group" && endpoint.pin === "input") {
    const alreadyLinked = node.targets.some(t =>
      t.kind === target.kind &&
      t.kind !== "sceneNode" && t.kind !== "animationNode" &&
      "vertexId" in t && "vertexId" in target ? t.vertexId === target.vertexId :
      "edgeId" in t && "edgeId" in target ? t.edgeId === target.edgeId :
      false,
    );
    const nextTargets = alreadyLinked ? node.targets : [...node.targets, target];
    updateAnimationNode(host, { ...node, targets: nextTargets }, `Connected to ${node.name}`);
    selectAnimationNodes(host, [node.id], node.id);
    return;
  }
  if (node.kind === "animate" && endpoint.pin === "input") {
    updateAnimationNode(host, { ...node, target }, `Connected to ${node.name}`);
    selectAnimationNodes(host, [node.id], node.id);
    return;
  }
  uiStore.setMessage("That animation input does not accept this target");
}

function connectMultipleTargetsToAnimation(host: EditorCommandHost, targets: AnimationTarget[], endpoint: AnimationEndpoint): void {
  const node = animationGraph().nodes.find(candidate => candidate.id === endpoint.nodeId);
  if (!node) {
    uiStore.setMessage("Animation connection target not found");
    return;
  }

  if (node.kind === "group" && endpoint.pin === "input") {
    const existingKeys = new Set(node.targets.map(t => JSON.stringify(t)));
    const newTargets = targets.filter(t => !existingKeys.has(JSON.stringify(t)));
    updateAnimationNode(host, { ...node, targets: [...node.targets, ...newTargets] }, `Connected ${targets.length} targets to ${node.name}`);
    selectAnimationNodes(host, [node.id], node.id);
    return;
  }
  uiStore.setMessage("That animation input does not accept multiple targets");
}

export function connectSceneObjectToAnimation(host: EditorCommandHost, sourceNodeId: string, endpoint: AnimationEndpoint): void {
  const node = animationGraph().nodes.find(candidate => candidate.id === endpoint.nodeId);
  const source = findSceneNode(documentStore.getDocument(), sourceNodeId);
  if (!node || !source) {
    uiStore.setMessage("Animation connection target not found");
    return;
  }

  if (node.kind === "group" && endpoint.pin === "input") {
    const targets = node.targets.some(target => target.kind === "sceneNode" && target.nodeId === sourceNodeId)
      ? node.targets
      : [...node.targets, { kind: "sceneNode" as const, nodeId: sourceNodeId }];
    updateAnimationNode(host, { ...node, targets }, `Connected ${source.name} to ${node.name}`);
    selectAnimationNodes(host, [node.id], node.id);
    return;
  }
  if (node.kind === "animate" && endpoint.pin === "input") {
    updateAnimationNode(host, { ...node, target: { kind: "sceneNode", nodeId: sourceNodeId } }, `Connected ${source.name} to ${node.name}`);
    selectAnimationNodes(host, [node.id], node.id);
    return;
  }
  if (node.kind === "animate" && endpoint.pin === "dependencyInput") {
    const dependencyIndex = endpoint.index ?? node.properties.findIndex(property =>
      property.kind === "fitToBBox"
      || property.kind === "pin"
      || property.kind === "followPath"
      || property.kind === "orbit"
      || property.kind === "lookAt",
    );
    const properties = dependencyIndex >= 0
      ? node.properties.map((property, index) => {
          if (index !== dependencyIndex || !["fitToBBox", "pin", "followPath", "orbit", "lookAt"].includes(property.kind)) return property;
          if (property.kind === "fitToBBox" || property.kind === "orbit" || property.kind === "lookAt") return { ...property, target: { kind: "sceneNode" as const, nodeId: sourceNodeId } };
          if (property.kind === "followPath") return { ...property, pathTarget: { kind: "sceneNode" as const, nodeId: sourceNodeId } };
          return { ...property, dependencyTarget: { kind: "sceneNode" as const, nodeId: sourceNodeId } };
        })
      : [...node.properties, { id: createId("animProp"), kind: "fitToBBox" as const, pad: 0, target: { kind: "sceneNode" as const, nodeId: sourceNodeId } }];
    updateAnimationNode(host, { ...node, properties }, `Connected ${source.name} to ${node.name}`);
    selectAnimationNodes(host, [node.id], node.id);
    return;
  }
  uiStore.setMessage("That animation input does not accept scene objects");
}

export function addAnimateProperty(host: EditorCommandHost, nodeId = host.currentSelection().primarySelectedAnimationNodeId): void {
  const target = animationGraph().nodes.find(node => node.id === nodeId);
  if (!target || target.kind !== "animate") return;
  updateAnimationNode(host, {
    ...target,
    properties: [...target.properties, { id: createId("animProp"), kind: "opacity", value: 1 }],
  }, "Added animation property");
}

export function deleteSelectedAnimationSelection(host: EditorCommandHost): boolean {
  const graph = animationGraph();
  const selection = host.currentSelection();
  const selectedObjectConnectionKeys = selectionStore.selectedObjectConnectionKeys.getValue();
  if (selectedObjectConnectionKeys.length > 0) {
    const context = host.commandContext();
    for (const key of selectedObjectConnectionKeys) {
      const parsed = parseObjectLinkKey(key);
      if (!parsed) continue;
      const { target, animNodeId, pin, index } = parsed;
      const node = graph.nodes.find(candidate => candidate.id === animNodeId);
      if (!node) continue;
      const targetKeyStr = animationTargetKey(target);
      if (node.kind === "animate" && pin === "input") {
        if (node.target && animationTargetKey(node.target) === targetKeyStr) {
          host.history.execute(new UpdateAnimationNodeCommand(node.id, node, { ...node, target: undefined }, "Deleted object connection"), context);
        }
      } else if (node.kind === "animate" && pin === "dependencyInput") {
        const properties = node.properties.map((property, propertyIndex) => {
          if (propertyIndex !== index) return property;
          if (property.kind === "fitToBBox" && property.target && animationTargetKey(property.target) === targetKeyStr) return { ...property, target: undefined };
          if (property.kind === "pin" && property.dependencyTarget && animationTargetKey(property.dependencyTarget) === targetKeyStr) return { ...property, dependencyTarget: undefined };
          if (property.kind === "followPath" && property.pathTarget && animationTargetKey(property.pathTarget) === targetKeyStr) return { ...property, pathTarget: undefined };
          if (property.kind === "orbit" && property.target && animationTargetKey(property.target) === targetKeyStr) return { ...property, target: undefined };
          if (property.kind === "lookAt" && property.target && animationTargetKey(property.target) === targetKeyStr) return { ...property, target: undefined };
          return property;
        });
        host.history.execute(new UpdateAnimationNodeCommand(node.id, node, { ...node, properties }, "Deleted object connection"), context);
      } else if (node.kind === "group" && pin === "input") {
        host.history.execute(new UpdateAnimationNodeCommand(node.id, node, {
          ...node,
          targets: node.targets.filter(t => animationTargetKey(t) !== targetKeyStr),
        }, "Deleted object connection"), context);
      }
    }
    selectionStore.setSelectedObjectConnectionKeys([]);
    host.commit(context.document, "Deleted object connections");
    return true;
  }

  const selectedConnections = graph.connections.filter(connection => (selection.selectedAnimationConnectionIds ?? []).includes(connection.id));
  if (selectedConnections.length > 0) {
    const context = host.commandContext();
    const nextSelection = host.selectionSnapshot({ selectedAnimationConnectionIds: [] });
    host.history.execute(new DeleteAnimationConnectionsCommand(selectedConnections), context, { afterSelection: nextSelection });
    host.applySelectionSnapshot(nextSelection);
    host.commit(context.document, "Deleted animation connections");
    return true;
  }

  const selectedNodes = graph.nodes.filter(node => (selection.selectedAnimationNodeIds ?? []).includes(node.id));
  if (selectedNodes.length === 0) return false;
  const context = host.commandContext();
  const nextSelection = host.selectionSnapshot({
    selectedAnimationNodeIds: [],
    primarySelectedAnimationNodeId: undefined,
    selectedAnimationConnectionIds: [],
  });
  const bypasses = motionSequenceBypassConnections(graph, selectedNodes);
  const command = bypasses.length > 0
    ? new TransactionCommand("Deleted animation nodes", [new DeleteAnimationNodesCommand(selectedNodes), ...bypasses.map(connection => new UpsertAnimationConnectionCommand(connection))])
    : new DeleteAnimationNodesCommand(selectedNodes);
  host.history.execute(command, context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, "Deleted animation nodes");
  return true;
}

export function bakeActiveAnimationToFrame(host: EditorCommandHost, rootNodeId: string | undefined, timeMs: number): void {
  const frameId = animationBakeFrameId(documentStore.getDocument(), rootNodeId);
  if (!frameId) {
    uiStore.setMessage("No animation frame selected");
    return;
  }
  uiStore.patchAnimation({ playing: false });
  try {
    const context = host.commandContext();
    host.history.execute(new BakeAnimationFrameCommand(frameId, timeMs, rootNodeId), context);
    host.commit(context.document, "Baked animation to frame");
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Bake failed");
  }
}

function connectionWithAvailableMotionInput(graph: AnimationGraph, connection: AnimationConnection): AnimationConnection {
  if (connection.to.pin !== "motionInput") return connection;
  const requestedIndex = connection.to.index ?? 0;
  const occupied = graph.connections.some(candidate =>
    candidate.id !== connection.id && candidate.to.nodeId === connection.to.nodeId && candidate.to.pin === "motionInput" && (candidate.to.index ?? 0) === requestedIndex,
  );
  if (!occupied) return { ...connection, to: { ...connection.to, index: requestedIndex } };
  const used = new Set(graph.connections
    .filter(candidate => candidate.to.nodeId === connection.to.nodeId && candidate.to.pin === "motionInput")
    .map(candidate => candidate.to.index ?? 0));
  let index = 0;
  while (used.has(index)) index += 1;
  return { ...connection, to: { ...connection.to, index } };
}

function motionSequenceBypassConnections(graph: AnimationGraph, deletedNodes: AnimationGraphNode[]): AnimationConnection[] {
  const deletedIds = new Set(deletedNodes.map(node => node.id));
  const deletedMotionIds = new Set(deletedNodes.filter(node => node.kind === "motion").map(node => node.id));
  if (deletedMotionIds.size === 0) return [];
  const bypasses: AnimationConnection[] = [];
  const finalConnections = graph.connections.filter(connection => !deletedIds.has(connection.from.nodeId) && !deletedIds.has(connection.to.nodeId));
  const hasFinalConnection = (from: AnimationEndpoint, to: AnimationEndpoint) =>
    finalConnections.some(connection => sameAnimationEndpoint(connection.from, from) && sameAnimationEndpoint(connection.to, to)) ||
    bypasses.some(connection => sameAnimationEndpoint(connection.from, from) && sameAnimationEndpoint(connection.to, to));
  const targetHasSurvivingConnection = (to: AnimationEndpoint) =>
    finalConnections.some(connection => sameAnimationEndpoint(connection.to, to)) ||
    bypasses.some(connection => sameAnimationEndpoint(connection.to, to));
  const incoming = graph.connections.filter(connection =>
    deletedMotionIds.has(connection.to.nodeId) &&
    connection.to.pin === "motionSequenceInput" &&
    !deletedIds.has(connection.from.nodeId) &&
    isGreenSequenceOutputEndpoint(graph, connection.from),
  );
  for (const connection of incoming) {
    for (const target of downstreamMotionSequenceTargets(graph, connection.to.nodeId, deletedMotionIds, deletedIds, new Set())) {
      if (connection.from.nodeId === target.nodeId || hasFinalConnection(connection.from, target) || targetHasSurvivingConnection(target)) continue;
      bypasses.push({ id: createId("animConn"), from: structuredClone(connection.from), to: structuredClone(target) });
    }
  }
  return bypasses;
}

function downstreamMotionSequenceTargets(graph: AnimationGraph, motionNodeId: string, deletedMotionIds: Set<string>, deletedIds: Set<string>, visited: Set<string>): AnimationEndpoint[] {
  if (visited.has(motionNodeId)) return [];
  visited.add(motionNodeId);
  const targets: AnimationEndpoint[] = [];
  for (const connection of graph.connections.filter(connection =>
    connection.from.nodeId === motionNodeId && connection.from.pin === "motionSequenceOutput" && connection.to.pin === "motionSequenceInput",
  )) {
    if (deletedMotionIds.has(connection.to.nodeId)) {
      targets.push(...downstreamMotionSequenceTargets(graph, connection.to.nodeId, deletedMotionIds, deletedIds, new Set(visited)));
    } else if (!deletedIds.has(connection.to.nodeId)) {
      targets.push(structuredClone(connection.to));
    }
  }
  return targets;
}

function isGreenSequenceOutputEndpoint(graph: AnimationGraph, endpoint: AnimationEndpoint): boolean {
  const node = graph.nodes.find(candidate => candidate.id === endpoint.nodeId);
  return (node?.kind === "animation" && endpoint.pin === "output") || (node?.kind === "motion" && endpoint.pin === "motionSequenceOutput");
}

function sameAnimationEndpoint(left: AnimationEndpoint, right: AnimationEndpoint): boolean {
  return left.nodeId === right.nodeId && left.pin === right.pin && (left.index ?? 0) === (right.index ?? 0);
}
